/**
 * CentralizedCache - Simple Centralized Caching System
 * 
 * This module provides a simple, centralized caching system that integrates
 * with GQLPagedQuery to cache data only when all properties are loaded.
 * 
 * Key Features:
 * - Hash-based cache keys generated from request body using SHA-256
 * - Only caches when all data is fetched (no partial caching)
 * - Session-based caching that persists until user logout
 * - Automatic cleanup of expired entries (only for non-session caches)
 * 
 * Dual-Layer Caching Architecture:
 * 
 * This cache is used at both API and GQL layers to optimize different use cases:
 * 
 * 1. API Layer Caching (fetchPaginatedProperties):
 *    - Caches individual page results (e.g., page 0, page 1, etc.)
 *    - Used by Table component for paginated data display
 *    - Cache key includes paging parameters for page-specific results
 *    - Provides immediate performance for page-by-page navigation
 * 
 * 2. GQL Layer Caching (GQLPagedQuery.cacheCompleteData):
 *    - Caches complete datasets (all pages combined)
 *    - Used by Map component which needs all properties at once
 *    - Cache key excludes paging parameters for complete dataset
 *    - Provides long-term performance for bulk data operations
 * 
 * Why Dual Layers?
 * - Map Component: Needs ALL properties for map markers → Uses GQL layer cache
 * - Table Component: Needs paginated data (20-50 per page) → Uses API layer cache
 * 
 * This architecture optimizes for both use cases without duplicating cache logic.
 * 
 * @file CentralizedCache.js
 * @version 1.0.0
 * @since 2024
 */

/**
 * Simple hash function to generate cache keys from request body
 * @param {string} requestBody - JSON stringified request body
 * @returns {string} Hash key
 */
import sha256 from 'crypto-js/sha256';

/**
 * Generates a hash key using SHA-256 for the given request body string.
 * @param {string} requestBody - JSON stringified request body
 * @returns {string} Hash key (first 32 chars of hex digest)
 */
function generateHashKey(requestBody) {
  // Use SHA-256 to generate a strong hash, then take first 32 chars for brevity
  return sha256(requestBody).toString().slice(0, 32);
}

/**
 * CentralizedCache class for managing API response caching
 */
class CentralizedCache {
  constructor(options = {}) {
    this.maxSize = options.maxSize || 200; // Increased from 100 to 200 for large datasets
    this.defaultTTL = options.defaultTTL || 600000; // 10 minutes default TTL (increased from 5 minutes)
    this.cleanupInterval = options.cleanupInterval || 30000; // 30 seconds cleanup interval (more frequent)
    this.sessionBased = options.sessionBased !== false; // Default to session-based caching
    
    // Cache storage: Map<hashKey, {data, timestamp, ttl, isSessionCache}>
    this.cache = new Map();
    
    // Start cleanup interval (only cleans non-session caches)
    this.startCleanupInterval();
    
    console.log('CentralizedCache initialized with maxSize:', this.maxSize, 'sessionBased:', this.sessionBased);
  }

  /**
   * Generates a cache key from the request body
   * @param {Object} requestBody - The request body object (will be JSON stringified internally)
   * @returns {string} Cache key
   */
  generateKey(requestBody) {
    const requestString = JSON.stringify(requestBody);
    return generateHashKey(requestString);
  }

  /**
   * Gets cached data if available and valid
   * @param {string} key - Cache key
   * @returns {Object|null} Cached data or null if not found/expired
   */
  get(key) {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    // Check if expired (only for non-session caches)
    if (!entry.isSessionCache && Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  /**
   * Sets data in cache with TTL
   * @param {string} key - Cache key
   * @param {Object} data - Data to cache
   * @param {number} [ttl] - Time to live in milliseconds (ignored for session caches)
   * @param {boolean} [isSessionCache] - Whether this is a session cache (persists until logout)
   */
  set(key, data, ttl = this.defaultTTL, isSessionCache = this.sessionBased) {
    // Check if we need to evict entries
    if (this.cache.size >= this.maxSize) {
      this.evictOldest();
    }
    
    const entry = {
      data,
      timestamp: Date.now(),
      ttl: isSessionCache ? Number.MAX_SAFE_INTEGER : ttl, // Session caches never expire
      isSessionCache
    };
    
    this.cache.set(key, entry);
  }

  /**
   * Evicts the oldest cache entry
   */
  evictOldest() {
    if (this.cache.size === 0) return;
    
    let oldestKey = null;
    let oldestTime = Date.now();
    let largestEntryKey = null;
    let largestEntrySize = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      // Track oldest entry
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
        oldestKey = key;
      }
      
      // Track largest entry (for memory optimization)
      const entrySize = this.estimateEntrySize(entry);
      if (entrySize > largestEntrySize) {
        largestEntrySize = entrySize;
        largestEntryKey = key;
      }
    }
    
    // Prefer evicting large entries over just old entries for memory optimization
    const keyToEvict = largestEntrySize > 1000000 ? largestEntryKey : oldestKey; // 1MB threshold
    
    if (keyToEvict) {
      this.cache.delete(keyToEvict);
      console.log(`Evicted ${largestEntrySize > 1000000 ? 'large' : 'oldest'} cache entry (size: ${largestEntrySize} bytes)`);
    }
  }

  /**
   * Estimates the memory size of a cache entry
   */
  estimateEntrySize(entry) {
    try {
      return JSON.stringify(entry.data).length * 2; // Rough estimate: 2 bytes per character
    } catch (_e) {
      return 1000; // Default estimate if serialization fails
    }
  }

  /**
   * Cleans up expired cache entries (only non-session caches)
   */
  cleanup() {
    const now = Date.now();
    let cleanedCount = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      // Only clean up non-session caches that have expired
      if (!entry.isSessionCache && now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
        cleanedCount++;
      }
    }
    
    if (cleanedCount > 0) {
      console.log(`Cleaned up ${cleanedCount} expired cache entries`);
    }
  }

  /**
   * Starts the cleanup interval
   */
  startCleanupInterval() {
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.cleanupInterval);
  }

  /**
   * Stops the cleanup interval
   */
  stopCleanupInterval() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Clears all cache entries
   */
  clear() {
    this.cache.clear();
    console.log('Cache cleared');
  }

  /**
   * Clears only session cache entries (called on logout)
   */
  clearSessionCache() {
    let clearedCount = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.isSessionCache) {
        this.cache.delete(key);
        clearedCount++;
      }
    }
    
    console.log(`Cleared ${clearedCount} session cache entries`);
  }

  /**
   * Clears all cache entries (called on logout)
   */
  clearAllCache() {
    const totalEntries = this.cache.size;
    this.cache.clear();
    console.log(`Cleared all ${totalEntries} cache entries`);
  }

  /**
   * Gets cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    const now = Date.now();
    let expiredCount = 0;
    let sessionCacheCount = 0;
    
    for (const entry of this.cache.values()) {
      if (entry.isSessionCache) {
        sessionCacheCount++;
      } else if (now - entry.timestamp > entry.ttl) {
        expiredCount++;
      }
    }
    
    return {
      totalEntries: this.cache.size,
      sessionCacheEntries: sessionCacheCount,
      expiredEntries: expiredCount,
      maxSize: this.maxSize,
      sessionBased: this.sessionBased
    };
  }

  /**
   * Destroys the cache and cleans up resources
   */
  destroy() {
    this.stopCleanupInterval();
    this.clear();
    console.log('CentralizedCache destroyed');
  }
}

// Export singleton instance
export const centralizedCache = new CentralizedCache();

// Export utility functions for cache management
export const clearSessionCache = () => centralizedCache.clearSessionCache();
export const clearAllCache = () => centralizedCache.clearAllCache();
export const getCacheStats = () => centralizedCache.getStats();

// Export class for custom instances
export default CentralizedCache;
